import React, { useState, useCallback, forwardRef } from "react";
import { Input } from "../atoms/Input";
import "./AmountInput.scss";

export interface AmountInputProps {
  /**
   * Current amount value
   */
  value: string;

  /**
   * Change handler for the input
   */
  onChange: (value: string) => void;

  /**
   * Currency symbol (e.g. €, $)
   */
  currencySymbol?: string;

  /**
   * Preset amounts to display
   */
  presets?: number[];

  /**
   * Additional CSS class names on the root element
   */
  className?: string;

  /**
   * Placeholder text for the input
   */
  placeholder?: string;

  /**
   * Additional props for the input element
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;

  /**
   * Maximum value for the input. Will render the max preset with the string "max" instead of the amount.
   */
  max?: number;

  /**
   * Minimum value for the input. Will render the min preset with the string "min" instead of the amount.
   */
  min?: number;
}

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  ({ value, onChange, currencySymbol, presets, className = "", inputProps, max, min }, ref) => {
    const [selectedPreset, setSelectedPreset] = useState<number | null>(null);

    const handlePresetClick = useCallback(
      (amount: number) => {
        setSelectedPreset(amount);
        onChange(amount.toString());
      },
      [onChange],
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedPreset(null);
        onChange(e.target.value);
      },
      [onChange],
    );

    const getPresetText = (amount: number) => {
      if (amount === max) return "max";
      if (amount === min) return "min";
      return `${currencySymbol} ${amount}`;
    };

    return (
      <div className={`v2-amount-input ${className}`}>
        <div className="v2-amount-input__currency-label">{currencySymbol}</div>
        <Input
          ref={ref}
          type="number"
          value={value}
          onChange={handleInputChange}
          className={`v2-amount-input__input ${inputProps?.className || ""}`}
          min="0"
          step="any"
          {...inputProps}
        />
        {presets && (
          <div className="v2-amount-input__presets">
            {presets.map((amount) => (
              <button
                key={amount}
                className={`v2-amount-input__preset-button ${selectedPreset === amount ? "selected" : ""}`}
                onClick={() => handlePresetClick(amount)}
                type="button"
              >
                {getPresetText(amount)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

AmountInput.displayName = "AmountInput";
